import type { SectionTab } from 'v2/shared/components/section-tabs'
import type { EnterpriseRequestSource } from 'v2/shared/utils/buddy-enterprise-request'

export type LogsTab =
  | 'voice'
  | 'sip-trunking'
  | 'messages'
  | 'verify'
  | 'whatsapp'
  | 'recordings'
  | 'transcriptions'

export const LOGS_TAB_COPY: Record<LogsTab, SectionTab> = {
  voice: { title: 'Voice calls', description: 'Review call activity across your organization.' },
  'sip-trunking': { title: 'SIP trunks', description: 'Diagnose inbound and outbound SIP calls.' },
  recordings: { title: 'Recordings', description: 'Find and review recorded calls.' },
  transcriptions: {
    title: 'Transcriptions',
    description: 'Review transcripts of recorded calls.',
  },
  messages: { title: 'Messages', description: 'Review SMS and MMS delivery activity.' },
  whatsapp: { title: 'WhatsApp', description: 'Review WhatsApp messaging activity.' },
  verify: { title: 'Verify', description: 'Review verification attempts and delivery status.' },
}

/** The gateable tabs, and the Buddy upgrade request each one opens when locked. */
const GATED_TAB_SOURCES: Partial<Record<LogsTab, EnterpriseRequestSource>> = {
  messages: 'messaging',
  whatsapp: 'whatsapp',
  verify: 'verify',
}

export interface LogsTabLocks {
  /** Messaging has no per-channel org flag, so it stays on the plan gate. */
  isEnterpriseLocked: boolean
  isWhatsappLocked: boolean
  isVerifyLocked: boolean
}

export const isGatedTab = (tab: LogsTab): boolean => GATED_TAB_SOURCES[tab] !== undefined

/**
 * Availability is a separate question from locking: Verify is not sold in India, so
 * there the tab does not exist at all rather than rendering an upgrade CTA for a
 * product that cannot be bought. Synchronous (region comes from localStorage), so
 * unlike the locks it needs no loading window.
 */
export const isTabAvailable = (tab: LogsTab, isVerifyAvailable: boolean): boolean =>
  tab === 'verify' ? isVerifyAvailable : true

/**
 * A log tab has to lock on the same signal as the feature page it logs for, or an
 * account can use a channel and not read its own logs. WhatsApp and Verify are keyed
 * on their `*_disabled` org flag via useChannelLocks, exactly as `/whatsapp` and
 * `/verify` are; messaging has no such flag and stays on the plan gate.
 *
 * Returns the upgrade source rather than a boolean so the tab's label and its click
 * behaviour are decided once and cannot disagree.
 */
export const resolveTabLockSource = (
  tab: LogsTab,
  locks: LogsTabLocks,
): EnterpriseRequestSource | undefined => {
  const source = GATED_TAB_SOURCES[tab]
  if (!source) return undefined
  if (tab === 'whatsapp') return locks.isWhatsappLocked ? source : undefined
  if (tab === 'verify') return locks.isVerifyLocked ? source : undefined
  return locks.isEnterpriseLocked ? source : undefined
}

/** What a click on a tab should do, once readiness is taken into account. */
export type TabClickAction =
  | { type: 'ignore' }
  | { type: 'upgrade'; source: EnterpriseRequestSource }
  | { type: 'navigate' }

/**
 * Every lock reads false until the profile lands, so a gated tab clicked inside that
 * window looks unlocked: it would navigate, flash the loader and bounce to voice
 * instead of opening the upgrade request. Ignore the click rather than act on an
 * answer we do not have yet - the window is one profile fetch.
 */
export const resolveTabClick = (
  tab: LogsTab,
  locks: LogsTabLocks,
  locksReady: boolean,
): TabClickAction => {
  if (isGatedTab(tab) && !locksReady) return { type: 'ignore' }
  const source = resolveTabLockSource(tab, locks)
  return source ? { type: 'upgrade', source } : { type: 'navigate' }
}

/**
 * The page subtitle lists the log types on offer, so it is a surface like any other:
 * naming Verify where Verify does not exist advertises a product the customer cannot
 * buy. Derived from the same flag as the tab so the two cannot drift.
 */
export const logsSubtitle = (isVerifyAvailable: boolean): string =>
  isVerifyAvailable
    ? 'View your voice, SIP trunking, message, verify, WhatsApp, recording, and transcription logs'
    : 'View your voice, SIP trunking, message, WhatsApp, recording, and transcription logs'
